/**
 * Toaster: one Radix provider and one viewport (10-16; two regions used to overlap).
 * Shows `queueOrder[0]` only. Radix owns the timer: it starts when the bar shows and
 * pauses on hover, focus and window blur. Errors announce assertive, the rest polite.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";

import {
  IntentToast,
  INTENT_SR_PREFIX,
  ToastProvider,
  ToastViewport,
} from "@/components/shared/ui/toast";
import {
  queueOrder,
  useReducedMotion,
  useToast,
  type ToasterToast,
} from "@/queries/useToast";

const UNDO_TICK_MS = 1000;

/** Screen-reader text; the prefix carries status without the label (WCAG 1.4.1). */
function announcementText(t: ToasterToast): string {
  const heading =
    typeof t.heading === "string"
      ? t.heading
      : String(t.heading ?? "Notification");
  const body = typeof t.body === "string" ? t.body : undefined;
  const prefix = INTENT_SR_PREFIX[t.intent];
  const base = body ? `${prefix} ${heading}. ${body}` : `${prefix} ${heading}`;
  if (t.intent === "progress") return `${heading}, in progress`;
  if (t.intent === "successUndoable" && t.undo) {
    const s = Math.round(t.undo.windowMs / 1000);
    return `${base} Undo available for ${s} seconds.`;
  }
  return base;
}

const actionClass =
  "inline-flex min-h-6 shrink-0 items-center px-1 text-[13px] font-medium text-teal-border underline decoration-1 underline-offset-[3px] focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-border rounded";

/** At most one action: Undo, Retry, the error's fix link, or the warning's own. */
function renderAction(
  t: ToasterToast,
  remainingMs: number | null,
): React.ReactNode {
  if (t.intent === "successUndoable" && t.undo) {
    const secs = Math.max(0, Math.ceil((remainingMs ?? 0) / 1000));
    return (
      <>
        <span
          className="shrink-0 font-mono text-[11px] tabular-nums text-paper/60"
          aria-hidden="true"
        >
          {secs}s
        </span>
        <button
          type="button"
          className={actionClass}
          onClick={async () => {
            try {
              await t.undo?.onUndo();
            } finally {
              t.onOpenChange?.(false);
            }
          }}
        >
          Undo
        </button>
      </>
    );
  }
  if (t.intent === "error" && t.retry) {
    return (
      <button type="button" className={actionClass} onClick={() => t.retry?.()}>
        Retry
      </button>
    );
  }
  if (t.intent === "error" && t.fix) {
    return (
      <Link to={t.fix.to} className={actionClass}>
        {t.fix.label}
      </Link>
    );
  }
  if (t.action) {
    return (
      <button
        type="button"
        className={actionClass}
        onClick={() => t.action?.onClick()}
      >
        {t.action.label}
      </button>
    );
  }
  return null;
}

const ToastRow: React.FC<{
  t: ToasterToast;
  waiting: number;
  reducedMotion: boolean;
}> = ({ t, waiting, reducedMotion }) => {
  const [paused, setPaused] = useState(false);
  const [remainingMs, setRemainingMs] = useState<number | null>(
    t.undo ? t.undo.windowMs : null,
  );

  // Undo seconds tick down with the Radix timer and stop while it is paused.
  const pausedRef = useRef(false);
  const lastTickRef = useRef(Date.now());
  useEffect(() => {
    if (t.intent !== "successUndoable" || !t.undo) return undefined;
    setRemainingMs(t.undo.windowMs);
    lastTickRef.current = Date.now();
    const handle = setInterval(() => {
      const now = Date.now();
      const delta = now - lastTickRef.current;
      lastTickRef.current = now;
      if (pausedRef.current) return;
      setRemainingMs((prev) =>
        prev == null ? null : Math.max(0, prev - delta),
      );
    }, UNDO_TICK_MS);
    return () => clearInterval(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t.id, t.intent, t.undo?.windowMs]);

  const onPause = useCallback(() => {
    pausedRef.current = true;
    setPaused(true);
  }, []);
  const onResume = useCallback(() => {
    pausedRef.current = false;
    setPaused(false);
  }, []);

  // Radix 1.2.6 never clears its close timer on unmount; a MORPH's old row would dismiss this id.
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const { onOpenChange } = t;
  const onRowOpenChange = useCallback(
    (open: boolean) => {
      if (mounted.current) onOpenChange?.(open);
    },
    [onOpenChange],
  );

  const timed = t.durationMs != null && t.durationMs > 0;
  return (
    <IntentToast
      intent={t.intent}
      heading={t.heading}
      body={t.body}
      action={renderAction(t, remainingMs)}
      waiting={waiting}
      timerMs={timed ? (t.durationMs as number) : undefined}
      paused={paused}
      open={t.open}
      onOpenChange={onRowOpenChange}
      onPause={onPause}
      onResume={onResume}
      reducedMotion={reducedMotion}
      duration={timed ? (t.durationMs as number) : Infinity}
    >
      <span className="sr-only">{announcementText(t)}</span>
    </IntentToast>
  );
};

export const Toaster: React.FC = () => {
  const { toasts } = useToast();
  const reducedMotion = useReducedMotion();

  // A closing bar finishes its fade before the next one shows, so two never share the spot.
  const closing = toasts.filter((t) => !t.open);
  const queue = queueOrder(toasts);
  const shown = closing.length > 0 ? closing.slice(-1) : queue.slice(0, 1);
  const waiting = Math.max(0, queue.length - (closing.length > 0 ? 0 : 1));

  return (
    <ToastProvider duration={Infinity} swipeDirection="down">
      {/* Radix reads the timer and the announcement once per node: every MORPH remounts. */}
      {shown.map((t) => (
        <ToastRow
          key={`${t.id}:${t.rev ?? 0}`}
          t={t}
          waiting={waiting}
          reducedMotion={reducedMotion}
        />
      ))}
      <ToastViewport />
    </ToastProvider>
  );
};
